import { z } from 'zod';
/**
 * The error envelope every API error returns (slice-00 §12, spec §26.2).
 *
 * The two message fields are not redundant and must not be collapsed:
 *
 *   message      for developers. Precise, technical, safe to log.
 *   userMessage  rendered directly to the end user, verbatim. Never a status
 *                code, never a stack trace, never "an error occurred". It says
 *                what happened and what to do next, in the words of someone who
 *                does not know what a 401 is.
 *
 * `userMessage` is written here rather than in the UI so the same wording ships
 * to apps/web, apps/field, and the future portal — and so a new endpoint cannot
 * invent a fifth way to phrase "your session expired".
 */
export const ERROR_CODES = [
    // --- authentication -------------------------------------------------------
    'AUTH_INVALID_CREDENTIALS',
    'AUTH_ACCOUNT_LOCKED',
    'AUTH_TOKEN_EXPIRED',
    'AUTH_TOKEN_INVALID',
    'AUTH_TOKEN_REVOKED',
    'AUTH_AUDIENCE_MISMATCH',
    'AUTH_SELECTION_TOKEN_INVALID',
    'AUTH_SELECTION_AGENCY_NOT_A_CANDIDATE',
    'AUTH_REFRESH_TOKEN_INVALID',
    'AUTH_REFRESH_TOKEN_REUSED',
    'AUTH_MFA_REQUIRED',
    'AUTH_MFA_CODE_REQUIRED',
    'AUTH_MFA_INVALID',
    'AUTH_MFA_ALREADY_ENROLLED',
    'AUTH_RATE_LIMITED',
    'AUTH_PASSWORD_RESET_TOKEN_INVALID',
    'AUTH_PASSWORD_TOO_WEAK',
    // --- authorization --------------------------------------------------------
    'FORBIDDEN_MISSING_PERMISSION',
    // --- clients --------------------------------------------------------------
    'CLIENT_DISCHARGE_IS_ITS_OWN_ACTION',
    'IMPORT_ALREADY_STARTED',
    'IMPORT_MAPPING_INCOMPLETE',
    'IMPORT_FILE_UNREADABLE',
    // --- scheduling -----------------------------------------------------------
    'SCHEDULE_CONFLICT',
    'CAREGIVER_DOUBLE_BOOKED',
    'VISIT_CHANGED',
    'VISIT_NOT_EDITABLE',
    'VISIT_TRANSITION_REFUSED',
    'SHIFT_PATTERN_NOT_EDITABLE',
    'CALLOUT_NOT_POSSIBLE',
    'VISIT_NOT_OPEN',
    'OFFER_ALREADY_FILLED',
    'OFFER_NOT_AVAILABLE',
    'CARE_PLAN_DRAFT_EXISTS',
    'CARE_PLAN_VERSION_LOCKED',
    'CARE_PLAN_NOT_READY',
    'BULK_JOB_NOT_READY',
    // --- generic --------------------------------------------------------------
    'NOT_FOUND',
    'VALIDATION_FAILED',
    'INTERNAL_ERROR',
];
export const errorCodeSchema = z.enum(ERROR_CODES);
export const apiErrorSchema = z.object({
    code: errorCodeSchema,
    /** For developers and logs. */
    message: z.string(),
    /** Rendered to the end user as-is. */
    userMessage: z.string(),
    /** Field-level detail for validation failures; empty otherwise. */
    details: z.record(z.array(z.string())).optional(),
    requestId: z.string(),
    docsUrl: z.string().url(),
});
/**
 * The user-facing wording for every code, and the HTTP status it carries.
 *
 * Kept as data rather than scattered through `throw` sites so the whole set can
 * be read at once — which is the only way to notice that two codes disclose
 * different amounts of information about the same failure.
 */
export const ERROR_CATALOGUE = {
    // Deliberately one code for "no such email" and "wrong password". They are the
    // same response, byte for byte, because any difference between them is an
    // account-enumeration oracle (§7).
    AUTH_INVALID_CREDENTIALS: {
        status: 401,
        message: 'Credential verification failed.',
        userMessage: 'That email and password combination did not work. Check both and try again.',
    },
    AUTH_ACCOUNT_LOCKED: {
        status: 423,
        message: 'Account is locked after repeated failed sign-in attempts.',
        userMessage: 'This account is locked for 15 minutes after too many failed attempts. Wait, then try again — or ask an administrator at your agency to unlock it.',
    },
    AUTH_TOKEN_EXPIRED: {
        status: 401,
        message: 'Access token has expired.',
        userMessage: 'Your session has expired. Sign in again to continue.',
    },
    AUTH_TOKEN_INVALID: {
        status: 401,
        message: 'Access token failed verification.',
        userMessage: 'Your session is no longer valid. Sign in again to continue.',
    },
    AUTH_TOKEN_REVOKED: {
        status: 401,
        message: 'Access token has been revoked.',
        userMessage: 'You have been signed out. Sign in again to continue.',
    },
    AUTH_AUDIENCE_MISMATCH: {
        status: 401,
        message: 'Token audience does not match this API surface.',
        userMessage: 'This account cannot be used in this app. Sign in with the right app for your role.',
    },
    AUTH_SELECTION_TOKEN_INVALID: {
        status: 401,
        message: 'Selection token is missing, expired, or already used.',
        userMessage: 'That took too long, or the link was already used. Sign in again to pick your agency.',
    },
    AUTH_SELECTION_AGENCY_NOT_A_CANDIDATE: {
        status: 401,
        message: 'Requested agency is not in the verified candidate set for this selection token.',
        userMessage: 'That agency is not available for this sign-in. Sign in again and pick from the list.',
    },
    AUTH_REFRESH_TOKEN_INVALID: {
        status: 401,
        message: 'Refresh token is unknown, expired, or revoked.',
        userMessage: 'Your session has ended. Sign in again to continue.',
    },
    AUTH_REFRESH_TOKEN_REUSED: {
        status: 401,
        message: 'An already-rotated refresh token was presented; the token family has been revoked.',
        userMessage: 'For your security we ended every session on this account, because a sign-in token was used twice. Sign in again — and if this keeps happening, tell your administrator.',
    },
    AUTH_MFA_REQUIRED: {
        status: 401,
        message: 'Multi-factor authentication is required for this role and is not enrolled.',
        userMessage: 'Your role requires an authenticator app. Set one up to finish signing in.',
    },
    AUTH_MFA_CODE_REQUIRED: {
        status: 401,
        message: 'A TOTP code is required: this account has an enrolled authenticator.',
        userMessage: 'Enter the 6-digit code from your authenticator app to finish signing in.',
    },
    AUTH_MFA_INVALID: {
        status: 401,
        message: 'TOTP code failed verification.',
        userMessage: 'That code was not right. Check your authenticator app and enter the current code.',
    },
    AUTH_MFA_ALREADY_ENROLLED: {
        status: 409,
        message: 'Multi-factor authentication is already enrolled for this user.',
        userMessage: 'An authenticator app is already set up for this account. Remove it first if you want to use a different device.',
    },
    AUTH_RATE_LIMITED: {
        status: 429,
        message: 'Too many attempts from this address or for this account.',
        userMessage: 'Too many attempts. Wait a minute, then try again.',
    },
    AUTH_PASSWORD_RESET_TOKEN_INVALID: {
        status: 400,
        message: 'Password reset token is unknown, expired, or already used.',
        userMessage: 'That reset link has expired or was already used. Request a new one.',
    },
    AUTH_PASSWORD_TOO_WEAK: {
        status: 400,
        message: 'Password does not meet the minimum policy.',
        userMessage: 'Choose a longer password — at least 12 characters, and not one you use elsewhere.',
    },
    FORBIDDEN_MISSING_PERMISSION: {
        status: 403,
        message: 'Caller lacks the permission required for this operation.',
        userMessage: 'You do not have access to this. If you think you should, ask an administrator at your agency.',
    },
    CLIENT_DISCHARGE_IS_ITS_OWN_ACTION: {
        status: 400,
        message: 'DISCHARGED is not reachable through the status endpoint. Use POST /v1/clients/:id/discharge, which is gated on client:discharge.',
        userMessage: 'Discharging a client is its own action, not a status change — it ends their episode of care and stops billing. Use Discharge, and ask an administrator if you do not have it.',
    },
    IMPORT_ALREADY_STARTED: {
        status: 409,
        message: 'The import job is not in PREVIEWED state, so confirm is not available.',
        userMessage: 'That import has already been started. An import runs once — upload the file again to import it a second time.',
    },
    IMPORT_MAPPING_INCOMPLETE: {
        status: 400,
        message: 'The column mapping does not name every required field, or names a column not in the file.',
        userMessage: 'Some required columns are not matched up yet. Check the column matching above — every client needs a client number, a first and last name, and a date of birth.',
    },
    IMPORT_FILE_UNREADABLE: {
        status: 400,
        message: 'The uploaded file could not be parsed as CSV, or carried no header row.',
        userMessage: 'We could not read that file. Export it again as CSV, with the column names in the first row.',
    },
    // `details` carries one entry per refusing rule, keyed by its code, holding
    // the engine's sentence — the coordinator reads which rule and why, not a
    // generic "conflict".
    SCHEDULE_CONFLICT: {
        status: 409,
        message: 'The conflict engine refused this write: a blocking rule failed and was not overridden by someone permitted to.',
        userMessage: 'This visit cannot be saved as it is. Each reason below says what stopped it and what to change.',
    },
    // The database's exclusion constraint (visit_no_caregiver_overlap), not the
    // application check: two coordinators booked the same caregiver at the same
    // moment and this write lost the race (exit criterion 4).
    CAREGIVER_DOUBLE_BOOKED: {
        status: 409,
        message: 'visit_no_caregiver_overlap refused the write: the caregiver already holds an overlapping live visit.',
        userMessage: 'Someone has just booked this caregiver for an overlapping visit. Refresh the schedule, then choose another time or caregiver.',
    },
    VISIT_CHANGED: {
        status: 409,
        message: 'The visit version in the request is not the current version.',
        userMessage: 'Someone else changed this visit while you were editing it. Reload it to see their change, then make yours again.',
    },
    VISIT_NOT_EDITABLE: {
        status: 409,
        message: 'Only a SCHEDULED visit can be edited or reassigned.',
        userMessage: 'This visit can no longer be changed here — it has started, finished or been cancelled.',
    },
    VISIT_TRANSITION_REFUSED: {
        status: 409,
        message: 'The visit state machine refused this transition; details carries the reason.',
        userMessage: 'That change cannot be made to this visit. The reason below says why.',
    },
    SHIFT_PATTERN_NOT_EDITABLE: {
        status: 409,
        message: 'Only an ACTIVE, undeleted shift pattern can be edited or ended.',
        userMessage: 'This pattern has already ended or been replaced. Open the pattern that replaced it to make changes.',
    },
    CALLOUT_NOT_POSSIBLE: {
        status: 409,
        message: 'A call-out needs a SCHEDULED visit with a caregiver on it.',
        userMessage: 'Nobody is booked on this visit, or it has already started or ended, so there is no one to call out.',
    },
    VISIT_NOT_OPEN: {
        status: 409,
        message: 'Offers are for an open shift: a SCHEDULED visit with no caregiver.',
        userMessage: 'This visit already has a caregiver, or can no longer be booked. Offers go out for open shifts only.',
    },
    // The second claimant. Not refused — second, and told so plainly.
    OFFER_ALREADY_FILLED: {
        status: 409,
        message: 'The visit was claimed or assigned before this claim acquired its lock.',
        userMessage: 'Someone else has just taken this shift. Thank you for answering — it is filled.',
    },
    OFFER_NOT_AVAILABLE: {
        status: 409,
        message: 'The offer is not live: not yet released, expired, or already answered.',
        userMessage: 'This offer is no longer open. Check your other offers for shifts you can still take.',
    },
    CARE_PLAN_DRAFT_EXISTS: {
        status: 409,
        message: 'The care plan already has a draft version; one draft at a time.',
        userMessage: 'There is already a draft of this care plan. Open it and carry on, or discard it before starting another.',
    },
    CARE_PLAN_VERSION_LOCKED: {
        status: 409,
        message: 'An activated care plan version is immutable; write a new version instead.',
        userMessage: 'This version is already in use and cannot change. Revise the plan to make a new version.',
    },
    CARE_PLAN_NOT_READY: {
        status: 422,
        message: 'The draft cannot be activated as it stands; details name what to change.',
        userMessage: 'This draft cannot start yet. The reason below says what to change.',
    },
    BULK_JOB_NOT_READY: {
        status: 409,
        message: 'Only a previewed bulk job with at least one ready row can be committed or discarded.',
        userMessage: 'This bulk change is not waiting to be committed — it is still previewing, already done, or has nothing ready. Reload to see where it stands.',
    },
    NOT_FOUND: {
        status: 404,
        message: 'No such resource in this tenant.',
        userMessage: 'We could not find that. It may have been removed, or you may not have access to it.',
    },
    VALIDATION_FAILED: {
        status: 400,
        message: 'Request failed schema validation.',
        userMessage: 'Some of what you entered is not valid. Check the highlighted fields and try again.',
    },
    INTERNAL_ERROR: {
        status: 500,
        message: 'Unhandled server error.',
        userMessage: 'Something went wrong on our side. Try again in a moment — we have been notified.',
    },
};
//# sourceMappingURL=errors.js.map